import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Users, UserPlus, Trash2, Edit, Shield, Building2, KeyRound, ToggleLeft, ToggleRight, ShieldOff } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function UserManagementPage({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ full_name: '', role: 'analyst' });
  const [newUserData, setNewUserData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirm_password: '',
    role: 'analyst',
    organization_id: '',
    branch_id: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const isOrgAdmin = user?.role === 'org_admin';

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/users`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrganizations = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const response = await axios.get(`${API}/organizations`, { headers: getAuthHeaders() });
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to load organizations');
    }
  }, [isSuperAdmin]);

  const fetchBranches = useCallback(async (orgId) => {
    if (!orgId) {
      setBranches([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/organizations/${orgId}/branches`, { headers: getAuthHeaders() });
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to load branches');
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, [fetchUsers, fetchOrganizations]);

  // For org_admin, auto-load their org's branches
  useEffect(() => {
    if (isOrgAdmin && user?.organization_id) {
      fetchBranches(user.organization_id);
    }
  }, [isOrgAdmin, user?.organization_id, fetchBranches]);

  useEffect(() => {
    if (newUserData.organization_id) {
      fetchBranches(newUserData.organization_id);
    }
  }, [newUserData.organization_id, fetchBranches]);

  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.full_name || !newUserData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (newUserData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newUserData.password !== newUserData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const { confirm_password, ...payload } = newUserData;
      await axios.post(`${API}/users`, payload, { headers: getAuthHeaders() });
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setNewUserData({
        email: '',
        full_name: '',
        password: '',
        confirm_password: '',
        role: 'analyst',
        organization_id: '',
        branch_id: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/users/${editingUser.id}`, formData, { headers: getAuthHeaders() });
      toast.success('User updated successfully');
      setDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.post(`${API}/users/${userId}/toggle-active`, {}, { headers: getAuthHeaders() });
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle user status');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('This will generate a temporary password. Continue?')) return;

    try {
      const response = await axios.post(`${API}/users/${userId}/reset-password`, {}, { headers: getAuthHeaders() });
      toast.success(`Temporary password: ${response.data.temporary_password}`, { duration: 10000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleResetMFA = async (userId, email) => {
    if (!window.confirm(`Reset MFA for ${email}?\n\nThey will be prompted to re-enroll on their next login and all their active sessions will be signed out.`)) return;
    try {
      const response = await axios.post(`${API}/users/${userId}/reset-mfa`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message || 'MFA reset');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset MFA');
    }
  };

  const openEditDialog = (userData) => {
    setEditingUser(userData);
    setFormData({ full_name: userData.full_name, role: userData.role });
    setDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
      org_admin: 'bg-rose-100 text-rose-700 border-rose-200',
      admin: 'bg-rose-100 text-rose-700 border-rose-200',
      analyst: 'bg-blue-100 text-blue-700 border-blue-200',
      viewer: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    return config[role] || config.viewer;
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : '-';
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="users">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="users-title">
              User Management
            </h1>
            <p className="text-sm text-slate-600 mt-1">Create and manage user accounts</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading users...</div>
        ) : (
          <Card className="border border-slate-200 shadow-sm">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center">
                <Users className="w-5 h-5 text-slate-600 mr-2" />
                <h2 className="text-lg font-semibold text-slate-900">All Users ({users.length})</h2>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Role</th>
                    {isSuperAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Organization</th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-4 font-medium text-slate-900">{u.full_name}</td>
                      <td className="px-6 py-4 text-slate-600">{u.email}</td>
                      <td className="px-6 py-4">
                        <Badge className={getRoleBadge(u.role)}>
                          {u.role?.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-4 text-slate-600">
                          {u.organization_id ? getOrgName(u.organization_id) : '-'}
                        </td>
                      )}
                      <td className="px-6 py-4">
                        {u.is_active !== false ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Inactive</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(u)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleResetPassword(u.id)} title="Reset Password">
                            <KeyRound className="w-4 h-4" />
                          </Button>
                          {u.id !== user.id && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleActive(u.id, u.is_active !== false)}
                                title={u.is_active !== false ? 'Deactivate' : 'Activate'}
                              >
                                {u.is_active !== false ? (
                                  <ToggleRight className="w-4 h-4 text-green-600" />
                                ) : (
                                  <ToggleLeft className="w-4 h-4 text-slate-400" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResetMFA(u.id, u.email)}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                title="Reset MFA (emergency recovery)"
                                data-testid={`reset-mfa-${u.id}`}
                              >
                                <ShieldOff className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(u.id)}
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="new_email">Email *</Label>
                <Input
                  id="new_email"
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  placeholder="user@company.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new_name">Full Name *</Label>
                <Input
                  id="new_name"
                  value={newUserData.full_name}
                  onChange={(e) => setNewUserData({ ...newUserData, full_name: e.target.value })}
                  placeholder="John Smith"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new_password">Password *</Label>
                <PasswordInput
                  id="new_password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                  placeholder="Min. 6 characters"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new_confirm_password">Confirm Password *</Label>
                <PasswordInput
                  id="new_confirm_password"
                  value={newUserData.confirm_password}
                  onChange={(e) => setNewUserData({ ...newUserData, confirm_password: e.target.value })}
                  placeholder="Re-enter password"
                  className="mt-1"
                />
                {newUserData.password && newUserData.confirm_password && newUserData.password !== newUserData.confirm_password && (
                  <p className="text-xs text-rose-500 mt-1">Passwords do not match</p>
                )}
              </div>
              <div>
                <Label htmlFor="new_role">Role</Label>
                <Select
                  value={newUserData.role}
                  onValueChange={(value) => setNewUserData({ ...newUserData, role: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                    <SelectItem value="org_admin">Org Admin</SelectItem>
                    <SelectItem value="analyst">Analyst</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isSuperAdmin && (
                <div>
                  <Label htmlFor="new_org">Organization</Label>
                  <Select
                    value={newUserData.organization_id || "none"}
                    onValueChange={(value) => setNewUserData({ ...newUserData, organization_id: value === "none" ? "" : value, branch_id: '' })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Organization</SelectItem>
                      {organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Branch selector: visible for super_admin (when org selected) and org_admin */}
              {branches.length > 0 && (isSuperAdmin ? newUserData.organization_id : isOrgAdmin) && (
                <div>
                  <Label htmlFor="new_branch">Branch</Label>
                  <Select
                    value={newUserData.branch_id || "none"}
                    onValueChange={(value) => setNewUserData({ ...newUserData, branch_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="mt-1" data-testid="branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Branch</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateUser}>Create User</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger className="mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                      <SelectItem value="org_admin">Org Admin</SelectItem>
                      <SelectItem value="analyst">Analyst</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleUpdate} className="w-full">
                  Update User
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Role Descriptions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 border border-purple-200 bg-purple-50">
            <div className="flex items-center mb-2">
              <Shield className="w-4 h-4 text-purple-600 mr-2" />
              <h3 className="font-semibold text-purple-900 text-sm">Super Admin</h3>
            </div>
            <p className="text-xs text-purple-700">Full system access, manage all organizations and users</p>
          </Card>
          <Card className="p-4 border border-rose-200 bg-rose-50">
            <div className="flex items-center mb-2">
              <Building2 className="w-4 h-4 text-rose-600 mr-2" />
              <h3 className="font-semibold text-rose-900 text-sm">Org Admin</h3>
            </div>
            <p className="text-xs text-rose-700">Manage organization users, branches, and settings</p>
          </Card>
          <Card className="p-4 border border-blue-200 bg-blue-50">
            <div className="flex items-center mb-2">
              <Users className="w-4 h-4 text-blue-600 mr-2" />
              <h3 className="font-semibold text-blue-900 text-sm">Analyst</h3>
            </div>
            <p className="text-xs text-blue-700">Perform scans, manage API keys, export reports</p>
          </Card>
          <Card className="p-4 border border-slate-200 bg-slate-50">
            <div className="flex items-center mb-2">
              <Users className="w-4 h-4 text-slate-600 mr-2" />
              <h3 className="font-semibold text-slate-900 text-sm">Viewer</h3>
            </div>
            <p className="text-xs text-slate-700">Read-only access to dashboard and results</p>
          </Card>
        </div>
      </div>
    </Layout>
  );
}